import { EMPLOYEE_LIMIT } from './assessmentPricing';

export const ONSITE_DAY_RATE = 5000;
export const emptyBusiness = () => ({ name: '', employees: '', locations: '', states: '', onsiteDays: '' });
export const emptyPortfolioIndustry = () => ({ name: '', businesses: [emptyBusiness()] });

export function validatePortfolio(industries) {
  const errors = {};
  const industryNames = new Set();
  const totals = { employees: 0, businesses: 0, locations: 0, onsiteDays: 0 };
  if (!industries.length) errors.form = 'Add at least one industry.';
  industries.forEach((industry, i) => {
    const name = industry.name.trim().toLowerCase();
    if (!name) errors[`${i}.name`] = 'Enter an industry name.';
    else if (industryNames.has(name)) errors[`${i}.name`] = 'Group businesses in the same industry together.';
    industryNames.add(name);
    if (!industry.businesses.length) errors.form = 'Each industry needs at least one business.';
    const businessNames = new Set();
    industry.businesses.forEach((business, j) => {
      const prefix = `${i}.${j}.`;
      const businessName = business.name.trim().toLowerCase();
      if (!businessName) errors[prefix + 'name'] = 'Enter a business name.';
      else if (businessNames.has(businessName)) errors[prefix + 'name'] = 'Use a distinct business name within this industry.';
      businessNames.add(businessName);
      ['employees', 'locations', 'states'].forEach(key => {
        const value = Number(business[key]);
        if (String(business[key]).trim() === '' || !Number.isSafeInteger(value) || value < 1) errors[prefix + key] = 'Enter a whole number of at least 1.';
      });
      if (!errors[prefix + 'states'] && !errors[prefix + 'locations'] && Number(business.states) > Number(business.locations)) errors[prefix + 'states'] = 'States cannot exceed operating locations.';
      const days = Number(business.onsiteDays);
      if (String(business.onsiteDays).trim() === '' || !Number.isSafeInteger(days * 2) || days < 0 || !Number.isSafeInteger(days * ONSITE_DAY_RATE)) errors[prefix + 'onsiteDays'] = 'Enter zero or more days in half-day increments.';
      totals.businesses += 1;
      totals.employees += Number(business.employees);
      totals.locations += Number(business.locations);
      totals.onsiteDays += days;
    });
  });
  if (Object.keys(errors).length) return { status: 'invalid', errors };
  if (!Number.isSafeInteger(totals.employees) || !Number.isSafeInteger(totals.locations) || !Number.isSafeInteger(totals.onsiteDays * ONSITE_DAY_RATE)) return { status: 'invalid', errors: { form: 'Combined counts are too large. Review the inputs.' } };
  if (totals.employees > EMPLOYEE_LIMIT) return { status: 'custom', totals };
  return { status: 'valid', totals };
}

export const MIN_BUSINESS_FEE = 30000;
export const MAX_BUSINESS_FEE = 50000;
export const BUSINESS_PRICING_METHOD = 'Each business starts at $30,000 (up to 10 employees, one location, one state). Proposed scaling: add $10,000 × ln(1 + max(employees − 10, 0) / 50), $5,000 × ln(locations), and $2,500 × ln(states). The assessment fee is capped at $50,000 and rounded to the nearest $500. These are internal pricing assumptions, not market-validated coefficients. Onsite days × $5,000 are added separately and are not subject to the $50,000 assessment cap.';
const METHOD = BUSINESS_PRICING_METHOD + ' Industry groups organize the estimate without an extra surcharge. Business totals sum exactly to industry subtotals and the portfolio total.';

export function calculatePortfolio(industries) {
  const validation = validatePortfolio(industries);
  if (validation.status !== 'valid') return { ...validation, method: METHOD };
  const pricedIndustries = industries.map(industry => {
    const businesses = industry.businesses.map(business => {
      const employees = Number(business.employees);
      const locations = Number(business.locations);
      const states = Number(business.states);
      const onsiteDays = Number(business.onsiteDays);
      const employeeFee = Math.round(10000 * Math.log1p(Math.max(employees - 10, 0) / 50));
      const locationFee = Math.round(5000 * Math.log(locations));
      const stateFee = Math.round(2500 * Math.log(states));
      const raw = MIN_BUSINESS_FEE + employeeFee + locationFee + stateFee;
      const capped = Math.min(MAX_BUSINESS_FEE, raw);
      const assessment = Math.round(capped / 500) * 500;
      const onsite = onsiteDays * ONSITE_DAY_RATE;
      return { ...business, employees, locations, states, onsiteDays, assessment, onsite, total: assessment + onsite,
        components: [['Assessment foundation', MIN_BUSINESS_FEE], ['Employee coverage', employeeFee], ['Operating location coverage', locationFee], ['State coverage', stateFee], ['Assessment cap adjustment', capped - raw], ['Rounding adjustment', assessment - capped], ['Onsite assessment', onsite]] };
    });
    return { name: industry.name, businesses, total: businesses.reduce((sum, business) => sum + business.total, 0) };
  });
  const businesses = pricedIndustries.flatMap(industry => industry.businesses);
  const assessmentTotal = businesses.reduce((sum, business) => sum + business.assessment, 0);
  const onsiteTotal = validation.totals.onsiteDays * ONSITE_DAY_RATE;
  if (!Number.isSafeInteger(assessmentTotal + onsiteTotal)) return { status: 'invalid', method: METHOD, errors: { form: 'Total estimate is too large. Review onsite days.' } };
  return { status: 'estimated', method: METHOD, totals: validation.totals, assessmentTotal, onsiteTotal, total: assessmentTotal + onsiteTotal, industries: pricedIndustries };
}
